/**
 * Sprint 23.5 — HireDraftSpecialtiesTags.
 *
 * Free-text tag editor (Q12). No taxonomy v1; the AI suggests during
 * conversation and the user can add/remove via this control. Each tag has
 * an x button; adding is the input at the end (Enter / comma commits).
 */

import { useState, type CSSProperties, type KeyboardEvent } from "react";

export interface HireDraftSpecialtiesTagsProps {
  value: string[];
  onChange: (value: string[]) => void;
}

export function HireDraftSpecialtiesTags({ value, onChange }: HireDraftSpecialtiesTagsProps) {
  const [pending, setPending] = useState("");

  function add(raw: string) {
    const tag = raw.trim();
    if (!tag) return;
    if (value.includes(tag)) {
      setPending("");
      return;
    }
    onChange([...value, tag]);
    setPending("");
  }

  function remove(tag: string) {
    onChange(value.filter((t) => t !== tag));
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      add(pending);
    } else if (e.key === "Backspace" && pending.length === 0 && value.length > 0) {
      e.preventDefault();
      onChange(value.slice(0, -1));
    }
  }

  return (
    <div style={wrap}>
      <div style={labelText}>Specialties</div>
      <div style={tagRow}>
        {value.length === 0 && pending.length === 0 ? (
          <span style={emptyHint}>None yet — type and press Enter to add.</span>
        ) : null}
        {value.map((tag) => (
          <span key={tag} style={tagPill}>
            {tag}
            <button
              type="button"
              onClick={() => remove(tag)}
              style={removeBtn}
              aria-label={`Remove ${tag}`}
              title={`Remove ${tag}`}
            >
              <i className="ti ti-x" style={{ fontSize: 10 }} aria-hidden="true" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={pending}
          onChange={(e) => setPending(e.target.value)}
          onKeyDown={onKey}
          onBlur={() => add(pending)}
          placeholder={value.length === 0 ? "" : "Add…"}
          style={input}
          aria-label="Add specialty"
        />
      </div>
    </div>
  );
}

const wrap: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  paddingBottom: 10,
};

const labelText: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: "var(--color-text-tertiary)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const tagRow: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 6,
  padding: 6,
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  minHeight: 36,
};

const tagPill: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: 12,
  padding: "3px 4px 3px 8px",
  borderRadius: 12,
  background: "var(--color-background-secondary)",
  color: "var(--color-text-primary)",
  border: "var(--kuvio-border-default)",
};

const removeBtn: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 16,
  height: 16,
  borderRadius: 999,
  border: "none",
  background: "transparent",
  color: "var(--color-text-tertiary)",
  cursor: "pointer",
};

const input: CSSProperties = {
  flex: 1,
  minWidth: 100,
  fontSize: 13,
  padding: "2px 4px",
  border: "none",
  outline: "none",
  background: "transparent",
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
};

const emptyHint: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-tertiary)",
  fontStyle: "italic",
};
